import Vue from 'vue'
import Router from 'vue-router'
import { JWTService } from '@/shared/services/jwt'

// Containers
import PublicBaseLayout from '@/containers/PublicBaseLayout'
import PrivateBaseLayout from '@/containers/PrivateBaseLayout'

// Public Views
import Authentication from '@/views/public/auth/Authentication.vue'
import LostPassword from '@/views/public/password/lost/LostPassword.vue'
import ChangePassword from '@/views/public/password/change/ChangePassword.vue'
// import PageNotFound from '@/views/public/pagenotfound/PageNotFound.vue'

// Privates Views
import StartPage from '@/views/private/StartPage.vue'
import Sinister from '@/views/private/regulation/auto/sinister.vue'
import Distribution from '@/views/private/regulation/distribution/distribution.vue'
import Inspection from '@/views/private/inspection/inspection.vue'
import ExpertRoutes from '@/views/private/expertroutes/expertroutes.vue'
import ImportRegulationAuto from '@/views/private/regulation/imports/importregulationauto.vue'
import Lighthouse from '@/views/private/regulation/lighthouse/lighthouse.vue'
import AdmAccount from '@/views/private/access/account/account.vue'
import AdmGroups from '@/views/private/access/groups/groups.vue'
import AdmPermission from '@/views/private/access/permission/permission.vue'
import AdmAdminitrativeLog from '@/views/private/access/log/administrativelog.vue'
import Cfg from '@/views/private/configuration/configuration.vue'
import CfgArea from '@/views/private/configuration/area/area.vue'
import CfgBank from '@/views/private/configuration/bank/bank.vue'
import CfgHoliday from '@/views/private/configuration/holiday/holiday.vue'
import CfgInsurance from '@/views/private/configuration/insurance/insurance.vue'
import CfgInsured from '@/views/private/configuration/insured/insured-conf.vue'
import CfgBranch from '@/views/private/configuration/branch/branch.vue'
import CfgStage from '@/views/private/configuration/stage/stage.vue'
import CfgRegulationType from '@/views/private/configuration/regulationtype/regulationtype.vue'
import CfgDocumentType from '@/views/private/configuration/documenttype/documenttype.vue'
import CfgImageType from '@/views/private/configuration/imagetype/imagetype.vue'
import CfgBusinessType from '@/views/private/configuration/businesstype/businesstype.vue'
import CfgProduct from '@/views/private/configuration/product/product.vue'
import CfgAutoType from '@/views/private/configuration/autotype/autotype.vue'
import CfgBrand from '@/views/private/configuration/brand/brand.vue'
import CfgAutoModel from '@/views/private/configuration/automodel/automodel.vue'
import CfgMechanicalWorkshop from '@/views/private/configuration/mechanicalworkshop/mechanicalworkshop-conf.vue'
import CfgRegionService from '@/views/private/configuration/regionservice/regionservice.vue'
import CfgExpert from '@/views/private/configuration/expert/expert.vue'
import CfgSchedulingType from '@/views/private/configuration/schedulingtype/schedulingtype.vue'
import CfgSchedulingContact from '@/views/private/configuration/schedulingcontact/schedulingcontact.vue'
import CfgBudgetSystem from '@/views/private/configuration/budgetsystem/budgetsystem.vue'
import Reports from '@/views/private/reports/reports.vue'
import Monitoring from '@/views/private/monitoring/monitoring.vue'
import MyAccount from '@/views/private/myaccount/profile/profile.vue'
import ReportSLA from '@/views/private/reports/sla/sla.vue'

Vue.use(Router)

/**
 * @function assertLoggedIn
 * @description Middleware para ser executado antes da troca de rota para garantir
 * que os usuários estão autenticados para ver esta rota.
 * @param {Object} to A rota de onde o usuário está vindo.
 * @param {Object} from A rota que o usuário deseja acessar.
 * @param {Object} next A rota que será renderizada.
 */
const assertLoggedIn = (to, from, next) => {
  if (!JWTService.GetToken()) {
    next({ name: 'SignIn' })
    return
  }
  next()
}
/**
 * @function assertNotLoggedIn
 * @description Middleware para ser executado antes da troca de rota para garantir
 * que os usuários não estão autenticados para ver esta rota.
 * @param {Object} to A rota de onde o usuário está vindo.
 * @param {Object} from A rota que o usuário deseja acessar.
 * @param {Object} next A rota que será renderizada.
 */
const assertNotLoggedIn = (to, from, next) => {
  if (JWTService.GetToken()) {
    next({ name: 'StartPage' })
    return
  }
  next()
}

/**
 * @function route
 * @description Cria um objeto de rota.
 * @param {string} path O caminho da rota.
 * @param {string} name O nome da rota.
 * @param {Object} component O componente a ser renderizado na rota.
 * @param {Array} children As children routes desta rota.
 * @returns {Object} Um objeto de rota do vue-router.
 */
const route = (path, name, component, children, beforeEnter) => ({
  path,
  name,
  component,
  children,
  beforeEnter
})

/**
 * @function children
 * @description Transforma um objetos com rotas em array.
 * @param {...} routes As rotas para serem retornadas em forma de array.
 * @returns {Array} O array com as rotas.
 */
const children = (...routes) => routes

/**
 * @property adminRoutes
 * @description As rotas privadas da aplicação.
 * @type {Object}
 */
const adminRoutes = {
  path: '/',
  redirect: '/pagina-inicial',
  component: PrivateBaseLayout,
  beforeEnter: assertLoggedIn,
  children: children(
    route('/pagina-inicial', 'StartPage', StartPage),
    route('/regulacao/sinistros', 'Regulacao', Sinister),
    route('/regulacao/distribuicao', 'Distribuicao', Distribution),
    route('/regulacao/importacao', 'Importacao', ImportRegulationAuto),
    route('/regulacao/farol', 'Farol', Lighthouse),
    route('/administracao/contas', 'Contas', AdmAccount),
    route('/administracao/grupos', 'Grupos', AdmGroups),
    route('/administracao/permissoes', 'Permissoes', AdmPermission),
    route('/administracao/adminitrativelog', 'AdminitrativeLog', AdmAdminitrativeLog),
    route('/configuracoes/detalhes', 'Configuracoes', Cfg),
    route('/configuracoes/area', 'Area', CfgArea),
    route('/configuracoes/banco', 'Banco', CfgBank),
    route('/configuracoes/feriados', 'Feriados', CfgHoliday),
    route('/configuracoes/seguradora', 'Seguradora', CfgInsurance),
    route('/configuracoes/segurado', 'Segurado', CfgInsured),
    route('/configuracoes/filiais', 'Filiais', CfgBranch),
    route('/configuracoes/estagios', 'Estagio', CfgStage),
    route('/configuracoes/produtos', 'Produtos', CfgProduct),
    route('/configuracoes/tipo-de-vistoria', 'TipoVistoria', CfgRegulationType),
    route('/configuracoes/tipo-de-documento', 'TipoDocumento', CfgDocumentType),
    route('/configuracoes/tipo-de-imagens', 'TipoImage', CfgImageType),
    route('/configuracoes/tipo-de-negocio', 'TipoNegocio', CfgBusinessType),
    route('/configuracoes/veiculo/tipo', 'TipoVeiculo', CfgAutoType),
    route('/configuracoes/veiculo/fabricante', 'Fabricante', CfgBrand),
    route('/configuracoes/veiculo/modelo', 'Modelo', CfgAutoModel),
    route('/configuracoes/oficina', 'Oficina-Cfg', CfgMechanicalWorkshop),
    route('/configuracoes/atendimento/regioes', 'RegioesAtendimento', CfgRegionService),
    route('/configuracoes/regulador', 'Perito', CfgExpert),
    route('/configuracoes/formas-de-vistoria', 'FormasVistoria', CfgSchedulingType),
    route('/configuracoes/contato-de-vistoria', 'ContatoVistoria', CfgSchedulingContact),
    route('/configuracoes/sistema/orcamentacao', 'SistemaOrcamentacao', CfgBudgetSystem),
    route('/relatórios', 'Relatorios', Reports),
    route('/relatórios/sla', 'SLA', ReportSLA),
    route('/monitoria', 'Monitoria', Monitoring),
    route('/minha-conta', 'MinhaConta', MyAccount),
    route('/vistoria', 'Vistoria', Inspection),
    route('/roteirizador', 'Roteirizador', ExpertRoutes)
  )
}

/**
 * @property publicRoutes
 * @description As rotas públicas da aplicação. (Não requerem autenticação)
 * @type {Object}
 */

const publicRoutes = {
  path: '/',
  redirect: '/login',
  component: PublicBaseLayout,
  children: children(
    route('/login', 'SignIn', Authentication, null, assertNotLoggedIn),
    route('/redefinir-senha', 'LostPassword', LostPassword, null, assertNotLoggedIn),
    route('/redefinir-senha/token/:token', 'ChangePassword', ChangePassword, null, assertNotLoggedIn)
  )
}

export default new Router({
  mode: 'history',
  linkActiveClass: 'open active',
  scrollBehavior: () => ({ y: 0 }),
  routes: [
    publicRoutes,
    adminRoutes,
    {
      path: '*',
      component: PublicBaseLayout
    },
    {
      'method': 'GET',
      'path': '/static/fonts/:file',
      'type': 'passthrough',
      'service': 'static/fonts/:file'
    }
  ]
})



// WEBPACK FOOTER //
// ./src/router/index.js